// src/components/WaitWithHopeModal.tsx
//
// "No match waiting" module — two trigger points, one component.
//   mode="fullscreen"  Trigger A: post-payment success screen replacement.
//   mode="popup"       Trigger B: paid user adds a crush with no immediate match.
//
// DOM/token pattern: HandleHelpModal family (fixed inset-0 backdrop, white card,
// 3px ink border, 6px shadow, borderRadius 20, close button ✕ in popup mode).

import { useState } from "react";
import { CollapsibleSection } from "./CollapsibleSection";
import { ZodiyakBubble } from "./ZodiyakBubble";
import type { ZodiacSign } from "@/utils/zodiac";

// ── Edit these 7 entries to update tip content ────────────────────────────────
const AURA_TIPS = [
  {
    title: "Be genuinely curious about them",
    body: "Ask a question you actually want to know the answer to. People can tell when you're faking interest — and they can tell when you're not.",
  },
  {
    title: "Show up to the same places",
    body: "You don't need a reason to be somewhere. Just be there, and be yourself. Proximity builds familiarity, and familiarity builds comfort.",
  },
  {
    title: "Remember small details",
    body: "If they mentioned a test, a trip, or a favourite show, follow up later. It signals that they matter — and that you actually listen.",
  },
  {
    title: "Make them feel at ease",
    body: "Calm energy is magnetic. You don't need to be the loudest person in the room. Let conversations breathe.",
  },
  {
    title: "Have something going on for yourself",
    body: "Pursue something you're genuinely into. A person who's excited about their own life is always more interesting.",
  },
  {
    title: "Stay consistent, not intense",
    body: "Small, regular positive interactions beat one huge grand gesture every time. Just keep showing up.",
  },
  {
    title: "Let it be light",
    body: "Relax the outcome. When you stop treating every interaction as high stakes, you become easier to be around — and harder to forget.",
  },
] as const;
// ─────────────────────────────────────────────────────────────────────────────

const FONT_DISPLAY = "'Fredoka', sans-serif";
const FONT_BODY    = "'Nunito', sans-serif";
const INK          = "#1A1A1A";
const CORAL        = "#E05A3A";

const primaryBtn = {
  width: "100%",
  padding: "14px 24px",
  fontFamily: FONT_DISPLAY,
  fontSize: 18,
  fontWeight: 700,
  background: CORAL,
  color: "#FFFFFF",
  border: `3px solid ${INK}`,
  borderRadius: 100,
  boxShadow: `4px 4px 0px ${INK}`,
  cursor: "pointer",
  transition: "all 0.1s ease",
} as const;

const secondaryBtn = {
  width: "100%",
  padding: "13px 24px",
  fontFamily: FONT_DISPLAY,
  fontSize: 18,
  fontWeight: 700,
  background: "#FFFFFF",
  color: INK,
  border: `3px solid ${INK}`,
  borderRadius: 100,
  boxShadow: `4px 4px 0px ${INK}`,
  cursor: "pointer",
  transition: "all 0.1s ease",
} as const;

export interface WaitWithHopeModalProps {
  mode: "fullscreen" | "popup";
  // popup-mode only:
  isOpen?: boolean;
  onClose?: () => void;
  onPostHint?: () => void;
  // both modes:
  onAddCrush: () => void;
  // fullscreen-mode only:
  zodiacSign?: ZodiacSign | null;
  onDismiss?: () => void; // subtle skip link so fullscreen is never a trap
}

export default function WaitWithHopeModal({
  mode,
  isOpen,
  onClose,
  onPostHint,
  onAddCrush,
  zodiacSign,
  onDismiss,
}: WaitWithHopeModalProps) {
  const [showTips,   setShowTips]   = useState(false);
  const [openTipIdx, setOpenTipIdx] = useState<number | null>(null);

  const isFullscreen = mode === "fullscreen";

  if (!isFullscreen && !isOpen) return null;

  // ── Shared card body: hope beats ────────────────────────────────────────────
  const hopeBody = (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <p style={{ fontFamily: FONT_BODY, fontSize: 15, color: "#444", lineHeight: 1.75, margin: 0 }}>
        They're not here yet, and that's okay. You've already been waiting. Now you can wait with hope.
      </p>
      <p style={{ fontFamily: FONT_BODY, fontSize: 15, color: "#444", lineHeight: 1.75, margin: 0 }}>
        Your spot never expires, so there's no window to miss. Whether they show up tomorrow or next month,
        your crush will be waiting.
      </p>
      <p style={{ fontFamily: FONT_BODY, fontSize: 15, color: "#444", lineHeight: 1.75, margin: 0 }}>
        In the meantime, go farm a little aura — and if there's someone else on your mind, add them to your list.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
        <button onClick={onAddCrush} style={primaryBtn}>Add a Crush</button>

        {/* "Post a Hint" — popup / Trigger B only, preserves the growth-loop nudge */}
        {!isFullscreen && onPostHint && (
          <button onClick={onPostHint} style={secondaryBtn}>Post a Hint</button>
        )}

        <button onClick={() => setShowTips(true)} style={secondaryBtn}>Crush Aura Tips</button>

        {/* Subtle dismiss — fullscreen only, so the screen is never a trap */}
        {isFullscreen && onDismiss && (
          <button
            onClick={onDismiss}
            style={{
              background: "none",
              border: "none",
              fontFamily: FONT_BODY,
              fontSize: 14,
              color: "#999",
              cursor: "pointer",
              textDecoration: "underline",
              textUnderlineOffset: 3,
              padding: "4px 0",
            }}
          >
            Skip for now
          </button>
        )}
      </div>
    </div>
  );

  // ── Shared card body: tips panel ─────────────────────────────────────────────
  const tipsBody = (
    <div>
      <button
        onClick={() => { setShowTips(false); setOpenTipIdx(null); }}
        style={{
          background: "none",
          border: "none",
          fontFamily: FONT_BODY,
          fontSize: 14,
          fontWeight: 700,
          color: CORAL,
          cursor: "pointer",
          padding: "0 0 14px 0",
          display: "block",
        }}
      >
        ← Back
      </button>

      <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 700, color: INK, margin: "0 0 16px" }}>
        Crush Aura Tips
      </h3>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {AURA_TIPS.map((tip, i) => (
          <CollapsibleSection
            key={i}
            title={tip.title}
            isOpen={openTipIdx === i}
            onToggle={() => setOpenTipIdx(openTipIdx === i ? null : i)}
          >
            <p style={{ fontFamily: FONT_BODY, fontSize: 14, color: "#555", lineHeight: 1.65, margin: 0, padding: "0 4px 8px" }}>
              {tip.body}
            </p>
          </CollapsibleSection>
        ))}
      </div>
    </div>
  );

  // ── Fullscreen mode (Trigger A: post-payment) ────────────────────────────────
  if (isFullscreen) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="max-w-2xl w-full flex flex-col items-center gap-6">
          {zodiacSign && !showTips && (
            <div className="flex items-start gap-3">
              <img
                src={`/SVG/zodiyak-${zodiacSign.toLowerCase()}.svg`}
                alt="Your Zodiyak guide"
                className="w-40 h-40 object-contain animate-bounce-in"
              />
              <ZodiyakBubble>
                <p className="text-2xl pl-4 font-semibold text-gray-800">Wait with hope.</p>
              </ZodiyakBubble>
            </div>
          )}

          <div
            style={{
              background: "#FFFFFF",
              border: `3px solid ${INK}`,
              borderRadius: 20,
              boxShadow: `6px 6px 0px ${INK}`,
              padding: 24,
              maxWidth: 420,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            {showTips ? tipsBody : hopeBody}
          </div>
        </div>
      </div>
    );
  }

  // ── Popup mode (Trigger B: add-crush no-match) ───────────────────────────────
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: "#FFFFFF",
          border: `3px solid ${INK}`,
          borderRadius: 20,
          boxShadow: `6px 6px 0px ${INK}`,
          padding: 28,
          width: "100%",
          maxWidth: 420,
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
          boxSizing: "border-box",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 12,
            right: 16,
            background: "none",
            border: "none",
            fontSize: 22,
            cursor: "pointer",
            color: INK,
            lineHeight: 1,
            padding: 4,
            zIndex: 1,
          }}
          aria-label="Close"
        >
          ✕
        </button>

        {showTips ? tipsBody : hopeBody}
      </div>
    </div>
  );
}
